import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { HelpCircle, ClipboardList, ChevronDown, ChevronUp, Trash2, GripVertical, Plus } from 'lucide-react';
interface CancellationSettingsPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
interface CancellationReason {
  id: string;
  reason: string;
  retentionOffer: string;
  showFeedback: boolean;
  requireFeedback: boolean;
  // Blank falls back to the global discount amount.
  discountOverride?: string;
}
// Shared chevron treatment used by every select on this page.
const selectClassName = 'w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white';
const selectStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundPosition: 'right 12px center',
  backgroundSize: '16px 16px'
};
const cancellationOfferOptions = [{
  value: 'no-offer',
  label: 'No offer - Continue to cancel'
}, {
  value: 'discount',
  label: 'Offer Discount - Reduce price temporarily'
}, {
  value: 'swap',
  label: 'Swap Products - Change subscription items'
}, {
  value: 'gift',
  label: 'Gift Subscription - Change shipping address'
}, {
  value: 'skip',
  label: 'Skip Next Order - Delay upcoming delivery'
}, {
  value: 'pause',
  label: 'Pause Subscription - Temporarily stop deliveries'
}, {
  value: 'reschedule',
  label: 'Change Delivery Date - Reschedule next order'
}, {
  value: 'address',
  label: 'Update Address - Modify shipping location'
}, {
  value: 'frequency',
  label: 'Update Frequency - Change delivery schedule'
}, {
  value: 'loyalty-points',
  label: 'Redeem Loyalty Points - Apply points to next order'
}, {
  value: 'non-cash',
  label: 'Non-Cash Reward - Bonus bottle, tasting or free freight'
}];
const membershipBenefitOptions = [{
  id: 'memberPricing',
  label: 'Member pricing'
}, {
  id: 'earlyAccess',
  label: 'Early access to new releases'
}, {
  id: 'allocationAccess',
  label: 'Allocation access'
}, {
  id: 'cellarDoor',
  label: 'Cellar door benefits'
}, {
  id: 'pointsBalance',
  label: 'Loyalty points balance'
}, {
  id: 'membershipLength',
  label: 'Length of membership'
}];
export function CancellationSettingsPage({
  currentPage = 'Tools',
  onNavigate
}: CancellationSettingsPageProps) {
  const navigate = useNavigate();
  // State
  const [activeTab, setActiveTab] = useState<'cancellation' | 'pause'>('cancellation');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Cancellation Tab State
  const [cancellationFlowType, setCancellationFlowType] = useState('retention');
  const [customInstructions, setCustomInstructions] = useState('');
  const [cancellationReasons, setCancellationReasons] = useState<CancellationReason[]>([{
    id: '1',
    reason: 'Too expensive',
    retentionOffer: 'discount',
    showFeedback: true,
    requireFeedback: false
  }, {
    id: '2',
    reason: 'Not using it enough',
    retentionOffer: 'pause',
    showFeedback: true,
    requireFeedback: false
  }, {
    id: '3',
    reason: 'Changed my mind',
    retentionOffer: 'no-offer',
    showFeedback: false,
    requireFeedback: false
  }]);
  const [discountAmount, setDiscountAmount] = useState(15);
  const [discountDuration, setDiscountDuration] = useState('next-2');
  const [showAdditionalMessages, setShowAdditionalMessages] = useState(false);
  // Benefits Page — shown before the member is asked why they are leaving.
  const [benefitsPageEnabled, setBenefitsPageEnabled] = useState(false);
  const [benefitsShown, setBenefitsShown] = useState<string[]>(['memberPricing', 'earlyAccess', 'allocationAccess']);
  const [benefitsHeadline, setBenefitsHeadline] = useState('');
  const [benefitsIntro, setBenefitsIntro] = useState('');
  const [fallbackOffer, setFallbackOffer] = useState('no-offer');
  const [maxPointsShare, setMaxPointsShare] = useState(50);
  const [usAutoRenewalLaw, setUsAutoRenewalLaw] = useState(false);
  // Pause Tab State
  const [pauseFlowType, setPauseFlowType] = useState('retention');
  const [pauseReasons, setPauseReasons] = useState<CancellationReason[]>([{
    id: '1',
    reason: 'Going on vacation',
    retentionOffer: 'no-offer',
    showFeedback: false,
    requireFeedback: false
  }, {
    id: '2',
    reason: 'Need a break',
    retentionOffer: 'discount',
    showFeedback: true,
    requireFeedback: false
  }, {
    id: '3',
    reason: 'Too much product',
    retentionOffer: 'skip',
    showFeedback: false,
    requireFeedback: false
  }]);
  const [defaultPauseDuration, setDefaultPauseDuration] = useState('');
  // Navigation handler
  const handleNavigate = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    } else {
      if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Tools') navigate('/management/tools');else navigate('/');
    }
  };
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const handleAddReason = (isCancel: boolean) => {
    const newReason: CancellationReason = {
      id: Date.now().toString(),
      reason: '',
      retentionOffer: 'no-offer',
      showFeedback: false,
      requireFeedback: false
    };
    if (isCancel) {
      setCancellationReasons([...cancellationReasons, newReason]);
    } else {
      setPauseReasons([...pauseReasons, newReason]);
    }
    saveSetting();
  };
  const handleDeleteReason = (id: string, isCancel: boolean) => {
    if (isCancel) {
      setCancellationReasons(cancellationReasons.filter(r => r.id !== id));
    } else {
      setPauseReasons(pauseReasons.filter(r => r.id !== id));
    }
    saveSetting();
  };
  const handleUpdateReason = (id: string, field: keyof CancellationReason, value: any, isCancel: boolean) => {
    const updateReasons = (reasons: CancellationReason[]) => reasons.map(r => r.id === id ? {
      ...r,
      [field]: value
    } : r);
    if (isCancel) {
      setCancellationReasons(updateReasons(cancellationReasons));
    } else {
      setPauseReasons(updateReasons(pauseReasons));
    }
    saveSetting();
  };
  const toggleBenefit = (id: string) => {
    setBenefitsShown(prev => prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id]);
    saveSetting();
  };
  const hasDiscountOffer = cancellationReasons.some(r => r.retentionOffer === 'discount') || fallbackOffer === 'discount';
  const discountReasons = cancellationReasons.filter(r => r.retentionOffer === 'discount');
  const hasLoyaltyOffer = cancellationReasons.some(r => r.retentionOffer === 'loyalty-points') || fallbackOffer === 'loyalty-points';
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-7716" data-mp="3m0osp74ehzms">
      {/* NavBar */}
      <div className="mb-6" data-id="element-7717" data-mp="3h2na0naw02bu">
        <NavBar tabs={[]} title="Cancellation Settings" subtitle="Configure how customers can cancel or pause their subscriptions. Set up retention offers to reduce churn." data-id="element-7718" data-mp="3h3yd0kwid61w" />
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm mb-6" data-id="element-7719" data-mp="381ys3hidhhto">
        <div className="flex gap-8 px-6" data-id="element-7720" data-mp="3346rlz3nwndv">
          <button onClick={() => setActiveTab('cancellation')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'cancellation' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7721" data-mp="16abxl5z9v4sg">
            Cancellation
          </button>
          <button onClick={() => setActiveTab('pause')} className={`h-12 text-[14px] font-medium border-b-2 transition-colors ${activeTab === 'pause' ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-7722" data-mp="f4q5lps7ieuz">
            Pause
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6" data-id="element-7723" data-mp="3nk04yq4pp5z2">
        {/* CANCELLATION TAB */}
        {activeTab === 'cancellation' && <>
            {/* Section 1: Cancellation Flow Type */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7724" data-mp="3dn55o50m71jq">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-7725" data-mp="3ilgfifqwtc1y">
                Cancellation Flow Type
              </h2>
              <div className="space-y-3" data-id="element-7726" data-mp="1bwk0n2za1djr">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${cancellationFlowType === 'direct' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7727" data-mp="1btfq7nv7xlkn">
                  <input type="radio" name="cancellationFlow" value="direct" checked={cancellationFlowType === 'direct'} onChange={e => {
                setCancellationFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7728" data-mp="2wdt4ug0mxwyg" />
                  <div className="flex-1" data-id="element-7729" data-mp="3k1kipo1tqi1u">
                    <div className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7730" data-mp="3k4qajb1u7y19">
                      Direct Cancellation
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-7731" data-mp="3u007vlhajh8p">
                      Customers can cancel their subscription immediately
                      without any retention offers or additional steps
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${cancellationFlowType === 'retention' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7732" data-mp="1uoh4rnio536y">
                  <input type="radio" name="cancellationFlow" value="retention" checked={cancellationFlowType === 'retention'} onChange={e => {
                setCancellationFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7733" data-mp="em1bey0jdihl" />
                  <div className="flex-1" data-id="element-7734" data-mp="12f1cytpko6x1">
                    <div className="flex items-center gap-2 mb-1" data-id="element-7735" data-mp="xdu6qz32vfsh">
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7736" data-mp="sndpf3w4za0u">
                        Retention Offers
                      </span>
                      <Badge variant="success" data-id="element-7737" data-mp="olad6d1n81ei">Recommended</Badge>
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-7738" data-mp="1l4hqqiq9pvve">
                      Present customers with alternative options (discounts,
                      pause, skip, etc.) based on their cancellation reason
                      before they cancel
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${cancellationFlowType === 'custom' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7739" data-mp="340wyzapgr4wg">
                  <input type="radio" name="cancellationFlow" value="custom" checked={cancellationFlowType === 'custom'} onChange={e => {
                setCancellationFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7740" data-mp="3hytx4bjkfjd8" />
                  <div className="flex-1" data-id="element-7741" data-mp="3vq8iuq4uxynz">
                    <div className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7742" data-mp="3vs33e3c7vorj">
                      Custom Instructions
                    </div>
                    <p className="text-[14px] text-[#64748B] mb-3" data-id="element-7743" data-mp="9hz7hxrs3vh3">
                      Display custom instructions to customers explaining how to
                      cancel their subscription (e.g., 'Please contact support')
                    </p>
                    {cancellationFlowType === 'custom' && <textarea value={customInstructions} onChange={e => {
                  setCustomInstructions(e.target.value);
                  saveSetting();
                }} placeholder="Enter custom cancellation instructions..." className="w-full h-24 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7744" data-mp="2fc879ua26ycz" />}
                  </div>
                </label>
              </div>
            </div>

            {/* Section 2: Benefits Page */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7745" data-mp="2t9ep9mmykt15">
              <div className="flex items-start justify-between gap-4 mb-2" data-id="element-7746" data-mp="13wtdezd5cmm3">
                <div data-id="element-7747" data-mp="yyrv4gnjorbo">
                  <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7748" data-mp="yzb1r17qvtae">
                    Benefits Page
                  </h2>
                  <p className="text-[14px] text-[#64748B]" data-id="element-7749" data-mp="3fjn4wk8tv46m">
                    Remind members what they'd be giving up before they're asked
                    why they're leaving.
                  </p>
                </div>
                <button onClick={() => {
              setBenefitsPageEnabled(!benefitsPageEnabled);
              saveSetting();
            }} role="switch" aria-checked={benefitsPageEnabled} aria-label="Enable Benefits Page" className={`w-11 h-6 rounded-full transition-colors duration-200 relative flex-shrink-0 mt-1 ${benefitsPageEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7750" data-mp="35t1y49j02tcm">
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${benefitsPageEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-7751" data-mp="2mupo6lk9lffz" />
                </button>
              </div>

              {benefitsPageEnabled && <div className="space-y-4 mt-6" data-id="element-7752" data-mp="l1ry2xhvh0yq">
                  <div data-id="element-7753" data-mp="2h5zhkoe1jpq9">
                    <span className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7754" data-mp="2h6hcnhoyvxwl">
                      Benefits to show
                    </span>
                    <div className="grid grid-cols-2 gap-3" data-id="element-7755" data-mp="285t77bdn35vb">
                      {membershipBenefitOptions.map(benefit => <label key={benefit.id} className="flex items-center gap-2 cursor-pointer" data-id="element-7756" data-mp="237rdzxmi1q64">
                          <input type="checkbox" checked={benefitsShown.includes(benefit.id)} onChange={() => toggleBenefit(benefit.id)} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-7757" data-mp="1y8ofyx4xsk7n" />
                          <span className="text-[14px] text-[#0F172A]" data-id="element-7758" data-mp="1pgw9exj19v8c">
                            {benefit.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div data-id="element-7759" data-mp="1fgvt7kggnv8d">
                    <label htmlFor="benefitsHeadline" className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7760" data-mp="1aikeowf80wjt">
                      Headline (optional)
                    </label>
                    <input id="benefitsHeadline" type="text" value={benefitsHeadline} onChange={e => setBenefitsHeadline(e.target.value)} onBlur={() => saveSetting()} placeholder="e.g., Here's what you'd be leaving behind" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7761" data-mp="10gzybvt8mc6k" />
                  </div>

                  <div data-id="element-7762" data-mp="mmz6zn4m7rg8">
                    <label htmlFor="benefitsIntro" className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7763" data-mp="moadhupg3db1">
                      Intro message (optional)
                    </label>
                    <textarea id="benefitsIntro" value={benefitsIntro} onChange={e => setBenefitsIntro(e.target.value)} onBlur={() => saveSetting()} placeholder="A short message shown above the benefits" className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7764" data-mp="wdache3y68p8" />
                  </div>

                  <p className="text-[12px] text-[#64748B]" data-id="element-7765" data-mp="3n1b84ioy0fbn">
                    This step is shown first, before the cancellation reasons.
                  </p>
                </div>}
            </div>

            {/* Section 3: Cancellation Reasons */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7766" data-mp="392lejd1c0xs1">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7767" data-mp="394elktfuxk0k">
                Cancellation Reasons
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7768" data-mp="3j1anggb937w5">
                Configure the reasons customers see when trying to cancel. Each
                reason can trigger different retention offers to help reduce
                churn.
              </p>

              <div className="space-y-4" data-id="element-7769" data-mp="ycmsmx9jjixb">
                {cancellationReasons.map((reason, index) => <div key={reason.id} className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-7770" data-mp="tb5ydklkaupp" data-mpi={index}>
                    <div className="flex items-center gap-3 mb-4" data-id="element-7771" data-mp="10tdl0c3owih7">
                      <GripVertical className="w-5 h-5 text-[#94A3B8] cursor-move" data-id="element-7772" data-mp="15ub7h0vqfyoi" />
                      <span className="text-[14px] font-medium text-[#64748B]" data-id="element-7773" data-mp="rus1admhexeu">
                        {index + 1}. Reason
                      </span>
                      <div className="flex-1" data-id="element-7774" data-mp="vw56qmlnwyl8" />
                      <button onClick={() => handleDeleteReason(reason.id, true)} className="p-2 hover:bg-[#FEE2E2] rounded-md transition-colors" data-id="element-7775" data-mp="1jkxzybawgsc5">
                        <Trash2 className="w-4 h-4 text-[#EF4444]" data-id="element-7776" data-mp="1ojr9xtqza17k" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-7777" data-mp="1eupymgvdo7zx">
                      <div data-id="element-7778" data-mp="1etetodq74u78">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7779" data-mp="1esmyh40igyhh">
                          Cancellation Reason
                        </label>
                        <input type="text" value={reason.reason} onChange={e => handleUpdateReason(reason.id, 'reason', e.target.value, true)} placeholder="e.g., Too expensive, Not using it enough" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7780" data-mp="27btw1kme29p7" />
                        <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7781" data-mp="13w040iq3a2tx">
                          This is what customers will see in the dropdown
                        </p>
                      </div>

                      <div data-id="element-7782" data-mp="1wjvzxl7u3qpn">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7783" data-mp="1wkozo28dlfya">
                          Retention Offer
                        </label>
                        <select value={reason.retentionOffer} onChange={e => handleUpdateReason(reason.id, 'retentionOffer', e.target.value, true)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-7784" data-mp="1mmrbfeqinj5o">
                          {cancellationOfferOptions.map(option => <option key={option.value} value={option.value} data-id="element-7785" data-mp="3allazwd5g4a1">
                              {option.label}
                            </option>)}
                        </select>
                        <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7786" data-mp="3kfkd4vd83o1z">
                          Choose what alternative to offer when this reason is
                          selected
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-7787" data-mp="3u8qjkmntx2u7">
                        <div data-id="element-7788" data-mp="3u64dyski0xem">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7789" data-mp="30ut1fbd39bv">
                            Show Feedback Box
                          </label>
                          <select value={reason.showFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'showFeedback', e.target.value === 'yes', true)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-7790" data-mp="bty5bka9tg4x">
                            <option value="yes" data-id="element-7791" data-mp="2k8eeqmpiquh9">Yes</option>
                            <option value="no" data-id="element-7792" data-mp="2facqlnd8k4yx">No</option>
                          </select>
                        </div>
                        <div data-id="element-7793" data-mp="2bakhn9jxjwt1">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7794" data-mp="2b76ew07ctll2">
                            Require Feedback
                          </label>
                          <select value={reason.requireFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'requireFeedback', e.target.value === 'yes', true)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-7795" data-mp="331qjbufau2kf">
                            <option value="yes" data-id="element-7796" data-mp="11arjfu5tkqet">Yes</option>
                            <option value="no" data-id="element-7797" data-mp="1196ow4ji97gy">No</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-[12px] text-[#64748B]" data-id="element-7798" data-mp="rdv7fegppm5i">
                        When enabled, customers must provide written feedback
                      </p>
                    </div>
                  </div>)}

                <button onClick={() => handleAddReason(true)} className="w-full h-[48px] flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] font-medium text-[#0EA5E9] hover:bg-[#F0F9FF] hover:border-[#0EA5E9] transition-all" data-id="element-7799" data-mp="idqc42osme7p">
                  <Plus className="w-4 h-4" data-id="element-7800" data-mp="3uwt16vso75m0" />
                  Add More Reason
                </button>

                <div className="border-t border-[#E2E8F0] pt-4" data-id="element-7801" data-mp="cqn0wqycm4zv">
                  <label htmlFor="fallbackOffer" className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7802" data-mp="8ot62zau3v7m">
                    Fallback Offer
                  </label>
                  <select id="fallbackOffer" value={fallbackOffer} onChange={e => {
                setFallbackOffer(e.target.value);
                saveSetting();
              }} className={selectClassName} style={selectStyle} data-id="element-7803" data-mp="mmq0bp1tfpj4">
                    {cancellationOfferOptions.map(option => <option key={option.value} value={option.value} data-id="element-7804" data-mp="rglxipa998d8">
                        {option.label}
                      </option>)}
                  </select>
                  <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7805" data-mp="10g8xd6nc6646">
                    Used when a member doesn't select a reason — for example when
                    the reason step is skipped or made optional.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 4: Discount Settings */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7806" data-mp="34mivynecbrhm">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7807" data-mp="38mavkalranau">
                Discount Settings
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7808" data-mp="2bzemo687sr01">
                Configure how discounts work when offered as retention
                incentives. These settings apply to all discount offers.
              </p>

              {!hasDiscountOffer ? <div className="p-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg text-center" data-id="element-7809" data-mp="2pxcyb3v5i9bu">
                  <p className="text-[14px] text-[#64748B]" data-id="element-7810" data-mp="8ke15ak2fan">
                    No discount offers configured. Add a discount retention
                    offer above to see discount-specific settings here.
                  </p>
                </div> : <div className="space-y-4" data-id="element-7811" data-mp="faan7pejos7k">
                  <div className="grid grid-cols-2 gap-4" data-id="element-7812" data-mp="abzi1rz735vo">
                    <div data-id="element-7813" data-mp="achjr44mmcaz">
                      <label htmlFor="globalDiscountAmount" className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7814" data-mp="5kzj6h6imdqj">
                        Default Discount Amount
                      </label>
                      <div className="flex items-center gap-2" data-id="element-7815" data-mp="3spp7qt2wl287">
                        <input id="globalDiscountAmount" type="number" value={discountAmount} onChange={e => {
                    setDiscountAmount(parseInt(e.target.value) || 0);
                    saveSetting();
                  }} className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7816" data-mp="3sr09kncsfzwo" />
                        <span className="text-[14px] text-[#64748B]" data-id="element-7817" data-mp="1gy1zdb4pd1a6">%</span>
                      </div>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7818" data-mp="1cwq87166k7zv">
                        Applied to every discount offer unless a reason overrides
                        it below.
                      </p>
                    </div>
                    <div data-id="element-7819" data-mp="2xh51htg63taa">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7820" data-mp="2xhwmkac528mb">
                        Discount Duration
                      </label>
                      <select value={discountDuration} onChange={e => {
                  setDiscountDuration(e.target.value);
                  saveSetting();
                }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '16px 16px'
                }} data-id="element-7821" data-mp="3p79lhr00meaz">
                        <option value="this-order" data-id="element-7822" data-mp="2b7etspqbpksk">This order only</option>
                        <option value="next-2" data-id="element-7823" data-mp="26a4tgf1ebopy">Next 2 orders</option>
                        <option value="next-3" data-id="element-7824" data-mp="1ij95xu3z3foi">Next 3 orders</option>
                        <option value="ongoing" data-id="element-7825" data-mp="1ihfxc99suldv">Ongoing</option>
                      </select>
                    </div>
                  </div>

                  {discountReasons.length > 0 && <div className="border-t border-[#E2E8F0] pt-4" data-id="element-7826" data-mp="1sawunn95aima">
                      <span className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7827" data-mp="1nic1mny2c9jt">
                        Per-Reason Discount Amount
                      </span>
                      <div className="space-y-3" data-id="element-7828" data-mp="uty38732bb7q">
                        {discountReasons.map(reason => <div key={reason.id} className="flex items-center gap-3" data-id="element-7829" data-mp="urtzpq92se7z">
                            <span className="flex-1 text-[14px] text-[#0F172A] truncate" data-id="element-7830" data-mp="9cg1ibt9ci0g">
                              {reason.reason || 'Untitled reason'}
                            </span>
                            <div className="flex items-center gap-2 w-[160px]" data-id="element-7831" data-mp="j783rsxsbt1k">
                              <input type="number" value={reason.discountOverride ?? ''} onChange={e => handleUpdateReason(reason.id, 'discountOverride', e.target.value, true)} placeholder={`${discountAmount}`} aria-label={`Discount amount for ${reason.reason || 'untitled reason'}`} className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7832" data-mp="o8nmhechpngs" />
                              <span className="text-[14px] text-[#64748B]" data-id="element-7833" data-mp="1bxqdvdwxeuoh">
                                %
                              </span>
                            </div>
                          </div>)}
                      </div>
                      <p className="text-[12px] text-[#64748B] mt-2" data-id="element-7834" data-mp="30i5qa44yjfca">
                        Leave blank to use the default amount.
                      </p>
                    </div>}
                </div>}
            </div>

            {/* Section 5: Default Retention Messages */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7835" data-mp="2mhuz0zzqjyql">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7836" data-mp="3k4ttd42ygulb">
                Default Retention Messages
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7837" data-mp="3f7bn5lgboth0">
                Set default messages for each retention offer type. These are
                used when no custom message is specified for a specific
                cancellation reason.
              </p>

              <div className="space-y-4" data-id="element-7838" data-mp="6qp28cai94kz">
                <div data-id="element-7839" data-mp="6ri2qvivexuo">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7840" data-mp="6s0b2qit52iy">
                    Discount Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! We would like to offer you a {{discountAmount}}% discount." onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7841" data-mp="13p5fiacnqabm" />
                </div>

                <div data-id="element-7842" data-mp="1mmyitdr70m9j">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7843" data-mp="1mm6khv67oafk">
                    Gift/Change Address Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! Would you prefer to give the subscription as a gift by changing the shipping address?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7844" data-mp="1vfa6wsyu3xmc" />
                </div>

                <div data-id="element-7845" data-mp="2k0ewqvkahg16">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7846" data-mp="2jzvf7fyjwtno">
                    Pause Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! Would you prefer to pause your subscription instead of cancelling?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7847" data-mp="2oyq3uyj79cfp" />
                </div>

                {/* Additional Messages (Collapsible) */}
                <div className="border-t border-[#E2E8F0] pt-4" data-id="element-7848" data-mp="1rbr2wblh5ic0">
                  <button onClick={() => setShowAdditionalMessages(!showAdditionalMessages)} className="flex items-center gap-2 text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-7849" data-mp="1rdkcnzrdwio8">
                    {showAdditionalMessages ? <ChevronUp className="w-4 h-4" data-id="element-7850" data-mp="1mcekq45jszfr" /> : <ChevronDown className="w-4 h-4" data-id="element-7851" data-mp="1mbla2s3gxpsc" />}
                    Additional Retention Offer Messages
                  </button>

                  {showAdditionalMessages && <div className="mt-4 space-y-4" data-id="element-7852" data-mp="3k8v2bh4s1s8b">
                      <div data-id="element-7853" data-mp="3k70env24fm0j">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7854" data-mp="3k6s6gggy14vo">
                          Product Swap Offer Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to swap out the products in your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7855" data-mp="3ac8goq4mmx58" />
                      </div>

                      <div data-id="element-7856" data-mp="3txmthn20k8i6">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7857" data-mp="3u0hepgsupxcn">
                          Skip Next Order Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! How about you just skip the next order of your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7858" data-mp="2ihoe71g8sq8i" />
                      </div>

                      <div data-id="element-7859" data-mp="1btn8m01du2l0">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7860" data-mp="1btwoguspqtcz">
                          Reschedule Delivery Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to change your subscription date?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7861" data-mp="1lpgh98bzdbxi" />
                      </div>

                      <div data-id="element-7862" data-mp="cwxq3m0kml2">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7863" data-mp="e81w4jdfwcp">
                          Update Shipping Address Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to change your shipping address?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7864" data-mp="2wacms1683b49" />
                      </div>

                      <div data-id="element-7865" data-mp="2dj3e1u4a0qdq">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7866" data-mp="2djuyc8sfb1op">
                          Change Delivery Frequency Message
                        </label>
                        <textarea defaultValue="We don't want to see you go! Would you prefer to change the frequency of your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7867" data-mp="2nhj21o80bjj0" />
                      </div>
                    </div>}
                </div>
              </div>
            </div>

            {/* Section 6: Loyalty Redemption Settings */}
            {hasLoyaltyOffer && <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7868" data-mp="doobs41idy5">
                <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7869" data-mp="5c88po2zjhoe">
                  Loyalty Redemption Settings
                </h2>
                <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7870" data-mp="sz81wgc47pga">
                  Controls how points can be applied when a member takes the
                  loyalty points offer.
                </p>

                <div className="space-y-4" data-id="element-7871" data-mp="o1y1y6r3nve7">
                  <div className="p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg space-y-2" data-id="element-7872" data-mp="o0wsg6pqfgyl">
                    <div className="flex items-center justify-between" data-id="element-7873" data-mp="34pilzhpmceio">
                      <span className="text-[14px] text-[#64748B]" data-id="element-7874" data-mp="38sorgn4wq1o3">
                        Points to currency rate
                      </span>
                      <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-7875" data-mp="1f8wyftwll665">
                        100 points = $1.00
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-7876" data-mp="15ilh95nwl0hb">
                      <span className="text-[14px] text-[#64748B]" data-id="element-7877" data-mp="10kts2jen7sgt">
                        Minimum redeemable points
                      </span>
                      <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-7878" data-mp="wkjjcyzguny9">
                        500 points
                      </span>
                    </div>
                    <button onClick={() => navigate('/loyalty/rules')} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-7879" data-mp="mjzg5qsdz9de">
                      Change in loyalty settings
                    </button>
                  </div>

                  <div className="max-w-[280px]" data-id="element-7880" data-mp="2bzpeirjj6y7x">
                    <label htmlFor="maxPointsShare" className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7881" data-mp="2c10hwq92e3yi">
                      Maximum Order Paid With Points
                    </label>
                    <div className="flex items-center gap-2" data-id="element-7882" data-mp="2oh0673hn2s7e">
                      <input id="maxPointsShare" type="number" min={0} max={100} value={maxPointsShare} onChange={e => {
                  setMaxPointsShare(parseInt(e.target.value) || 0);
                  saveSetting();
                }} className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7883" data-mp="2t9b7a9o48k1j" />
                      <span className="text-[14px] text-[#64748B]" data-id="element-7884" data-mp="2aia8pmqwqqii">%</span>
                    </div>
                    <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7885" data-mp="2fhwljv2tq4qs">
                      The largest share of an order value a member can cover with
                      points.
                    </p>
                  </div>
                </div>
              </div>}

            {/* Section 7: Compliance */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7886" data-mp="fwhs3y917bnh">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7887" data-mp="buntrwmb6jqh">
                Compliance
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7888" data-mp="pm1a71ox71ha">
                Adjust the cancellation flow to meet regional requirements.
              </p>

              <div className="flex items-start justify-between gap-4" data-id="element-7889" data-mp="zj72ued6n0jm">
                <div data-id="element-7890" data-mp="zljrtbhcyb66">
                  <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-7891" data-mp="ukmbz5jtlt7u">
                    US Automatic Renewal Law
                  </div>
                  {usAutoRenewalLaw && <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7892" data-mp="18jc7igqzrku3">
                      Members in the US will skip the cancellation reason step
                      and be shown retention offers immediately.
                    </p>}
                </div>
                <button onClick={() => {
              setUsAutoRenewalLaw(!usAutoRenewalLaw);
              saveSetting();
            }} role="switch" aria-checked={usAutoRenewalLaw} aria-label="Enable US Automatic Renewal Law compliance" className={`w-11 h-6 rounded-full transition-colors duration-200 relative flex-shrink-0 ${usAutoRenewalLaw ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7893" data-mp="35d72pd74b9h3">
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${usAutoRenewalLaw ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-7894" data-mp="1g34p2yzcitjz" />
                </button>
              </div>
            </div>
          </>}

        {/* PAUSE TAB */}
        {activeTab === 'pause' && <>
            {/* Section 1: Pause Flow Type */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7895" data-mp="x4sfjc4ivhnv">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-6" data-id="element-7896" data-mp="x6lo4wyp4byi">
                Pause Flow Type
              </h2>
              <div className="space-y-3" data-id="element-7897" data-mp="2rijp6bmuq1jb">
                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${pauseFlowType === 'direct' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7898" data-mp="2ri9uiaqv367a">
                  <input type="radio" name="pauseFlow" value="direct" checked={pauseFlowType === 'direct'} onChange={e => {
                setPauseFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7899" data-mp="272tgr9j27p6d" />
                  <div className="flex-1" data-id="element-7900" data-mp="1jdh0jppbeguk">
                    <div className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-7901" data-mp="1o6a3c8183f40">
                      Direct Pause
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-7902" data-mp="19f19fmqaf7l2">
                      Customers can pause their subscription immediately without
                      any retention offers
                    </p>
                  </div>
                </label>

                <label className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all ${pauseFlowType === 'retention' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-7903" data-mp="14gzpz0oy9692">
                  <input type="radio" name="pauseFlow" value="retention" checked={pauseFlowType === 'retention'} onChange={e => {
                setPauseFlowType(e.target.value);
                saveSetting();
              }} className="mt-1 w-5 h-5 text-[#0EA5E9] border-[#CBD5E1] focus:ring-[#0EA5E9]" data-id="element-7904" data-mp="gw8oafz89udt" />
                  <div className="flex-1" data-id="element-7905" data-mp="14qs09ln8nzn4">
                    <div className="flex items-center gap-2 mb-1" data-id="element-7906" data-mp="zu1o39ek1o6z">
                      <span className="text-[16px] font-semibold text-[#0F172A]" data-id="element-7907" data-mp="zrp8h2w9rpx3">
                        Retention Offers
                      </span>
                      <Badge variant="success" data-id="element-7908" data-mp="19ke890igjfcl">Recommended</Badge>
                    </div>
                    <p className="text-[14px] text-[#64748B]" data-id="element-7909" data-mp="1nib7y5s7k1vh">
                      Present customers with alternative options (discounts,
                      swap products, etc.) based on their reason for pausing
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Section 2: Pause Reasons */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7910" data-mp="p79yq0bj7hcn">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7911" data-mp="p5gns8tvyayu">
                Pause Reasons
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7912" data-mp="yxmgxlvviyfm">
                Configure the reasons customers see when trying to pause. Each
                reason can trigger different retention offers.
              </p>

              <div className="space-y-4" data-id="element-7913" data-mp="17yua5pjj992u">
                {pauseReasons.map((reason, index) => <div key={reason.id} className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-7914" data-mp="1cxdx9t9v4qws" data-mpi={index}>
                    <div className="flex items-center gap-3 mb-4" data-id="element-7915" data-mp="39pnwqwrxuqkc">
                      <GripVertical className="w-5 h-5 text-[#94A3B8] cursor-move" data-id="element-7916" data-mp="39rijmgmrssr2" />
                      <span className="text-[14px] font-medium text-[#64748B]" data-id="element-7917" data-mp="3eohc6uqscrn5">
                        {index + 1}. Reason
                      </span>
                      <div className="flex-1" data-id="element-7918" data-mp="32dq12b8fmoc2" />
                      <button onClick={() => handleDeleteReason(reason.id, false)} className="p-2 hover:bg-[#FEE2E2] rounded-md transition-colors" data-id="element-7919" data-mp="32bwsuri67w1y">
                        <Trash2 className="w-4 h-4 text-[#EF4444]" data-id="element-7920" data-mp="2sglck4r46gs3" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-7921" data-mp="3q29c2yiv9f7u">
                      <div data-id="element-7922" data-mp="3l4hgnuj0jayw">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7923" data-mp="3l47s8bdm9bvb">
                          Pause Reason
                        </label>
                        <input type="text" value={reason.reason} onChange={e => handleUpdateReason(reason.id, 'reason', e.target.value, false)} placeholder="e.g., Going on vacation, Need a break" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7924" data-mp="3c3t4z6h3dmp6" />
                      </div>

                      <div data-id="element-7925" data-mp="3tc242klhxo9f">
                        <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7926" data-mp="3tcbvmcm6w3h8">
                          Retention Offer
                        </label>
                        <select value={reason.retentionOffer} onChange={e => handleUpdateReason(reason.id, 'retentionOffer', e.target.value, false)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-7927" data-mp="3jm8t9lt6hd68">
                          <option value="no-offer" data-id="element-7928" data-mp="2efr48e7cqzn3">
                            No offer - Continue to pause
                          </option>
                          <option value="discount" data-id="element-7929" data-mp="2tbgtcl7u3sw1">Offer Discount</option>
                          <option value="swap" data-id="element-7930" data-mp="2oego2cit6u5z">Swap Products</option>
                          <option value="skip" data-id="element-7931" data-mp="20n1hg7k01qox">Skip Next Order</option>
                          <option value="reschedule" data-id="element-7932" data-mp="20l882ki04sd8">
                            Change Delivery Date
                          </option>
                          <option value="frequency" data-id="element-7933" data-mp="2admdbn8agh45">Update Frequency</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-7934" data-mp="25l2x0ii7v7vn">
                        <div data-id="element-7935" data-mp="1hw9ycea7mlxc">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7936" data-mp="1hvige69jcmpj">
                            Show Feedback Box
                          </label>
                          <select value={reason.showFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'showFeedback', e.target.value === 'yes', false)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-7937" data-mp="22l2ux24llfdt">
                            <option value="yes" data-id="element-7938" data-mp="3927tznjp6sjm">Yes</option>
                            <option value="no" data-id="element-7939" data-mp="390n08057jdmt">No</option>
                          </select>
                        </div>
                        <div data-id="element-7940" data-mp="xgxh7ou0ydyr">
                          <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7941" data-mp="sg1dpcdl4ttv">
                            Require Feedback
                          </label>
                          <select value={reason.requireFeedback ? 'yes' : 'no'} onChange={e => handleUpdateReason(reason.id, 'requireFeedback', e.target.value === 'yes', false)} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-7942" data-mp="nmoujhrodf8i">
                            <option value="yes" data-id="element-7943" data-mp="17ychqbwpgv6j">Yes</option>
                            <option value="no" data-id="element-7944" data-mp="u7zakbrn1ki9">No</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>)}

                <button onClick={() => handleAddReason(false)} className="w-full h-[48px] flex items-center justify-center gap-2 border-2 border-dashed border-[#CBD5E1] rounded-lg text-[14px] font-medium text-[#0EA5E9] hover:bg-[#F0F9FF] hover:border-[#0EA5E9] transition-all" data-id="element-7945" data-mp="k9jkmc4ckhac">
                  <Plus className="w-4 h-4" data-id="element-7946" data-mp="baxzqabekq6e" />
                  Add More Reason
                </button>
              </div>
            </div>

            {/* Section 3: Pause Settings */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7947" data-mp="25ki5pg4vp3jn">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7948" data-mp="29netrmd6truz">
                Pause Settings
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7949" data-mp="3q4neh0clb9vr">
                Configure how pause offers work when presented as retention
                incentives.
              </p>

              <div data-id="element-7950" data-mp="7sihrkfjsoez">
                <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7951" data-mp="307i8iopz4my">
                  Default Pause Duration (Cycles)
                </label>
                <input type="number" value={defaultPauseDuration} onChange={e => {
              setDefaultPauseDuration(e.target.value);
              saveSetting();
            }} placeholder="Leave empty to let customer choose" className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7952" data-mp="hn0okyg9h030" />
                <p className="text-[12px] text-[#64748B] mt-1" data-id="element-7953" data-mp="2sizey6ku8bg0">
                  Number of billing cycles to pause. Leave empty to let customer
                  choose.
                </p>
              </div>
            </div>

            {/* Section 4: Default Pause Retention Messages */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-7954" data-mp="1tvp8czx7q26p">
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-7955" data-mp="1p2mf6swckur1">
                Default Pause Retention Messages
              </h2>
              <p className="text-[14px] text-[#64748B] mb-6" data-id="element-7956" data-mp="1f5rga8w5wsc8">
                Set default messages for pause retention offers.
              </p>

              <div className="space-y-4" data-id="element-7957" data-mp="1b3mqxmcqyhve">
                <div data-id="element-7958" data-mp="165ber4yzbva0">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7959" data-mp="164tb3n9zmes2">
                    Discount Offer Message
                  </label>
                  <textarea defaultValue="We don't want to see you go! We would like to offer you a {{discountAmount}}% discount." onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7960" data-mp="118cnqvam44g1" />
                </div>

                <div data-id="element-7961" data-mp="381ts6ah9in9x">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7962" data-mp="38125jp5n7zvs">
                    Extended Pause Offer Message
                  </label>
                  <textarea defaultValue="Are you sure you want to pause your subscription?" onBlur={saveSetting} className="w-full h-20 px-3 py-2 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent resize-none" data-id="element-7963" data-mp="3hsy5z6v7c236" />
                </div>

                <div data-id="element-7964" data-mp="2u2ar21mvcc74">
                  <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-7965" data-mp="2u56shzxxdj0b">
                    Decline Pause Button Text
                  </label>
                  <input type="text" defaultValue="No, pause the subscription" onBlur={saveSetting} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-7966" data-mp="9btlos7fdiv0" />
                </div>
              </div>
            </div>
          </>}
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-7967" data-mp="2jera2kwnnblg">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-7968" data-mp="2ffigph5j1fqf">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-7969" data-mp="1rmifxnobevc1" />
            <div className="flex-1" data-id="element-7970" data-mp="1mql1xo0iy12f">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-7971" data-mp="1mnfbo5g5st54">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-7972" data-mp="1vq7ynwib6ip5">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}